import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Button,
  Tooltip,
  Grid,
  RadioGroup,
  Radio,
  FormLabel, CircularProgress,
  Stack,
  InputBase,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import { QR } from "../qrCode/components/qrUi";
import { stepCardSx, stepActionsSx, backBtnSx, nextBtnSx } from "../campaign/components/stepUi";
import DeleteIcon from "@mui/icons-material/Delete";
import Tabs from "../branchDepartment/components/tabs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../config/appRoutes";
import SnackbarMessage from "../../components/snackBar/snackBar";
import { createFormThunk, getOrgFormsThunk } from "./formsThunk";
import { getTagsThunk } from "../contact/contactThunk";
import { FieldMultiSelect } from "../../components/selections/fieldInputs";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

// embedded: render without the module header (e.g. inside a popup); initialOrgId pre-selects the organisation;
// onDone: called after a successful create instead of going to the forms list. The page itself passes none of these.
export default function DynamicFormBuilderStepper({ embedded = false, initialOrgId, onDone } = {}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { status, formFieldTypes = [] } = useSelector((state) => state.organizatiomForm);

  const steps = ["Select Organisation", "Build Form", "Preview & Submit"];
  const [activeStep, setActiveStep] = useState(0);
  const { getOrganizations, getTags = [] } = useSelector((state) => state.contacts);

  // Tags: contacts with any of these tags see this form (optional)
  const [selectedTags, setSelectedTags] = useState([]);
  const tagOptions = getTags.map((t) => ({ value: t.id, label: t.tag_name }));
  useEffect(() => {
    dispatch(getTagsThunk({ type: "tags" }));
  }, [dispatch]);


  const [selectedOrg, setSelectedOrg] = useState(null);

  // pre-select the organisation passed in (once the organisation list is loaded)
  useEffect(() => {
    if (initialOrgId && !selectedOrg) {
      const org = (getOrganizations || []).find((o) => String(o.id) === String(initialOrgId));
      if (org) setSelectedOrg(org);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrgId, getOrganizations]);
  const [formName, setFormName] = useState("");
  const [errors, setErrors] = useState({});

  const [mode, setMode] = useState("");
  const [fields, setFields] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });
  const [formValues, setFormValues] = useState({});


  const handleNext = () => {
    if (activeStep === 0) {
      let newErrors = {};
      if (!selectedOrg) {
        newErrors.selectedOrg = "Please select an organisation to continue.";
      }
      if (!formName || formName.trim() === "") {
        newErrors.formName = "Form Name is required.";
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setSnack({ open: true, message: Object.values(newErrors)[0], severity: "error" });
        return;
      }

      setActiveStep((prev) => prev + 1);
    }

    if (activeStep === 1) {
      let newErrors = {}; // object to collect errors

      if (fields.length === 0) {
        newErrors.mode = "Add at least one field before continuing.";
      }

      fields.forEach((f, index) => {
        // Check for empty label
        if (!f.label || f.label.trim() === "") {
          newErrors[`field_${index}_label`] = `Label is required for field ${index + 1}`;
        }

        // Check options for Radio, Checkbox, Dropdown
        if (
          (f.mode === "Radio Button" || f.mode === "Checkbox" || f.mode === "Dropdown") &&
          f.options.some((opt) => !opt || opt.trim() === "")
        ) {
          newErrors[`field_${index}_options`] = `All options must be filled for field ${index + 1}`;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors); // push all errors
        // optionally, show first error in snackbar
        setSnack({
          open: true,
          message: Object.values(newErrors)[0],
          severity: "error",
        });
        return;
      }

      // clear errors if everything is valid
      setErrors({});
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleReset = () => {
    setActiveStep(0);
    setSelectedOrg(null);
    setFields([]);
    setMode("");
    setFormValues({});
    setFormName("");
  };

  const handleAddField = (selectedType) => {

    if (!selectedType) return;

    const selectedItem = formFieldTypes.find(
      (item) => item.form_field_type === selectedType
    );

    const newField = {
      id: selectedItem?.id || "",
      mode: selectedItem?.form_field_type || "",
      label: "",
      options:
        ["radio", "checkbox", "Dropdown-Single", "Dropdown-Multi"].includes(selectedType)
          ? [""]
          : [],
      isRequired: false,
    };
    setFields((prev) => [...prev, newField]);
    setMode("");
    setErrors({ ...errors, mode: "" });
  };

  const handleFieldChange = (index, key, value) => {

    setFields((prev) => {
      const updated = [...prev];
      updated[index][key] = value;
      return updated;
    });

    const fieldErrorKey = `field_${index}_${key}`;
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldErrorKey];
      return newErrors;
    });
  };

  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    const updated = [...fields];
    updated[fieldIndex].options[optionIndex] = value;
    setFields(updated);
  };

  const handleAddOption = (index) => {
    const updated = [...fields];
    updated[index].options.push("");
    setFields(updated);
  };

  const handleDeleteField = (index, type) => {
    setFields(fields.filter((_, i) => i !== index));
    setSnack({
      open: true,
      message: `${type.charAt(0).toUpperCase() + type.slice(1)} mode deleted successfully.`,
      severity: "success",
    });
  };

  const handleDeleteOption = (fieldIndex, optionIndex) => {
    const updated = [...fields];
    updated[fieldIndex].options.splice(optionIndex, 1);
    setFields(updated);
  };

  const handleInputChange = (label, value) => {
    setFormValues((prev) => ({ ...prev, [label]: value }));
  };

  const disabledStyles = {
    background: "var(--disabled-color)",
    borderRadius: "10px",
    border: "1px var(--disabled-color)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      paddingLeft: 2,
      paddingRight: 2,
      border: "none"
    },
  }

  const renderPreviewField = (f, idx) => {
    const raw = formValues[f.label];
    const value =
      raw !== undefined
        ? raw
        : ["checkbox", "Dropdown-Multi"].includes(f.mode)
          ? []
          : "";

    return (
      <Grid item size={{ xs: 12, md: 4 }} key={idx}>
        {/* TEXT FIELD */}
        {["text", "textarea", "number", "date"].includes(f.mode.toLowerCase()) && (
          <>
            <FormLabel>{f.isRequired ? <span style={{ color: "red" }}>*</span> : ""}{f.label}</FormLabel>
            <TextField
              fullWidth
              size="small"
              value={f.mode}
              // type={f.mode === "textarea" ? "text" : "text"}
              type={f.mode === "date" ? "date" : "text"}
              disabled
              multiline={f.mode === "textarea"}
              // rows={f.mode === "textarea" ? 3 : 1}
              sx={disabledStyles}
            />
          </>
        )}

        {/* RADIO */}
        {f.mode === "radio" && (
          <Box>
            <Typography>{f.label}</Typography>
            <RadioGroup
              row
              value={f.mode}
              onChange={(e) => handleInputChange(f.label, e.target.value)}
              disabled
            >
              {f.options.map((opt, i) => (
                <FormControlLabel key={i} value={opt} control={<Radio />} label={opt}  />
              ))}
            </RadioGroup>
          </Box>
        )}

        {/* CHECKBOX */}
        {f.mode === "checkbox" && (
          <Box>
            <Typography sx={{ mb: 1 }}>{f.isRequired ? <span style={{ color: "red" }}>*</span> : ""}{f.label}</Typography>
            {f.options.map((opt, i) => {
              const checked = Array.isArray(value) && value.includes(opt);
              return (
                <FormControlLabel
                  disabled
                  value={f.mode}
                  key={i}
                  control={
                    <Checkbox
                      sx={{ ...disabledStyles, mt: 1 }}
                      checked={checked}
                      onChange={(e) => {
                        const prev = Array.isArray(formValues[f.label])
                          ? [...formValues[f.label]]
                          : [];
                        if (e.target.checked) prev.push(opt);
                        else prev.splice(prev.indexOf(opt), 1);
                        handleInputChange(f.label, prev);
                      }}
                    />
                  }
                  label={opt}
                />
              );
            })}
          </Box>
        )}

        {/* DROPDOWN */}
        {(f.mode === "Dropdown-Single" || f.mode === "Dropdown-Multi") && (
          <Box>
            <Typography sx={{ mb: 1 }}>{f.isRequired ? <span style={{ color: "red" }}>*</span> : ""}{f.label}</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{f.label}</InputLabel>
              <Select
                sx={disabledStyles}
                multiple={f.mode === "Dropdown-Multi"}
                value={value}
                onChange={(e) => handleInputChange(f.label, e.target.value)}
                label={f.mode}
                renderValue={(selected) =>
                  Array.isArray(selected) ? selected.join(", ") : selected
                }
              >
                {f.options.map((opt, i) => (
                  <MenuItem key={i} value={opt}>
                    {f.mode === "Dropdown-Multi" && (
                      <Checkbox
                        disabled
                        checked={Array.isArray(value) && value.indexOf(opt) > -1}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ ml: f.mode === "Dropdown-Multi" ? 1 : 0 }}
                    >
                      {opt}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Grid>
    );
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedFields = fields.map((field) => {
      const formatted = {
        field_type_id: field.id,
        label: field.label,
        isRequired: field.isRequired,
        length: field.field_length,
        options: [],
      };

      if (["Dropdown-Single", "Dropdown-Multi", "checkbox", "radio"].includes(field.mode)) {
        formatted.options = field.options.map((opt, i) => ({
          label: opt,
          value: i + 1,
        }));
      } else {
        formatted.options = field.options;
      }

      return formatted;
    });

    try {
      const result = await dispatch(
        createFormThunk({
          credentials: {
            "organization_id": selectedOrg?.id,
            "form_name": formName.trim(),
            "tag_ids": selectedTags.map((t) => t.value).toString(),
            "fields": formattedFields
          }
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        setSnack({
          open: true,
          message: result.payload.message,
          severity: "success",
        });
        dispatch(getOrgFormsThunk({ type: "getForms" }));

        if (onDone) onDone(); else navigate(APP_ROUTES.FORM.LIST);
      } else {
        setSnack({
          open: true,
          message: result.payload,
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: "Server error. Please try again.",
        severity: "error",
      });
    }
  };

  // ---------- Look (presentation only) ----------
  const labelSx = { display: "block", mb: 0.75, fontSize: 13, fontWeight: 500, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b" };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 42,
      borderRadius: 2.5,
      bgcolor: "#fff",
      fontSize: 14,
      "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
      "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(37,99,235,0.12)" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ef4444" },
    },
  };
  const fieldModeColor = (m = "") => {
    const v = m.toLowerCase();
    if (v.includes("dropdown")) return QR.purple;
    if (v.includes("radio")) return "#0891b2";
    if (v.includes("checkbox")) return QR.amber;
    if (v.includes("date")) return "#db2777";
    if (v.includes("number")) return QR.blue;
    return QR.main;
  };
  const Req = () => <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>*</Box>;

  const PageWrap = embedded ? Fragment : Tabs;

  return (
    <>
      <PageWrap>
        <Box sx={{ ...stepCardSx, bgcolor: "#fff", mt: 1 }}>
          {/* ---------- Step bar ---------- */}
          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: { xs: 1.5, sm: 2 } }}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ p: { xs: 0.75, sm: 1 }, borderRadius: 4, bgcolor: "#f8fafc", border: `1px solid ${QR.border}`, overflowX: "auto", "&::-webkit-scrollbar": { display: "none" } }}
            >
              {steps.map((label, i) => {
                const done = i < activeStep;
                const current = i === activeStep;
                return (
                  <Stack key={label} direction="row" alignItems="center" sx={{ flex: i < steps.length - 1 ? 1 : "0 0 auto", minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        flexShrink: 0,
                        pl: 0.5,
                        pr: { xs: current ? 1.5 : 0.5, sm: 1.75 },
                        py: 0.5,
                        borderRadius: 5,
                        bgcolor: current ? QR.blue : done ? `${QR.main}14` : "transparent",
                        boxShadow: current ? "0 6px 14px -6px rgba(37,99,235,0.7)" : "none",
                      }}
                    >
                      <Box sx={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, color: current ? QR.blue : "#fff", bgcolor: current ? "#fff" : done ? QR.main : "#cbd5e1" }}>
                        {done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : i + 1}
                      </Box>
                      <Typography noWrap sx={{ display: { xs: current ? "block" : "none", sm: "block" }, fontSize: 13.5, fontWeight: current ? 800 : 700, color: current ? "#fff" : done ? QR.main : "#94a3b8" }}>
                        {label}
                      </Typography>
                    </Stack>
                    {i < steps.length - 1 && (
                      <Box sx={{ flex: 1, minWidth: 16, height: 2, mx: 1, borderRadius: 1, bgcolor: done ? QR.main : "#e2e8f0" }} />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {/* Step 0: Select Organisation */}
            {activeStep === 0 && (
              <Grid container spacing={2.5}>
                {/* Organisation Select */}
                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography component="label" sx={labelSx}>Organisation<Req /></Typography>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <Select
                      value={selectedOrg || ""}
                      displayEmpty
                      error={!!errors?.selectedOrg}
                      IconComponent={KeyboardArrowDownRoundedIcon}
                      startAdornment={<ApartmentRoundedIcon sx={{ fontSize: 19, mr: 1, color: selectedOrg ? QR.blue : "#9ca3af" }} />}
                      renderValue={(v) => v ? v.organization_name : <Box component="span" sx={{ color: "#9ca3af" }}>Select organisation</Box>}
                      onChange={(e) => { setSelectedOrg(e.target.value); setErrors({ ...errors, selectedOrg: "" }); }}
                      MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: 2.5, border: `1px solid ${QR.border}`, boxShadow: "0 12px 28px -12px rgba(15,23,42,0.3)" } } }}
                    >
                      {getOrganizations.map((org) => (
                        <MenuItem key={org.id} value={org} sx={{ fontSize: 13.5 }}>{org.organization_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Form Name Input */}
                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography component="label" sx={labelSx}>Form Name<Req /></Typography>
                  <TextField
                    value={formName}
                    onChange={(e) => { setFormName(e.target.value); setErrors({ ...errors, formName: "" }); }}
                    size="small"
                    fullWidth
                    placeholder="e.g. Admission Enquiry"
                    error={!!errors.formName}
                    autoComplete="off"
                    InputProps={{ startAdornment: <DynamicFormOutlinedIcon sx={{ fontSize: 19, mr: 1, color: formName ? QR.blue : "#9ca3af" }} /> }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Tags: contacts with any of these tags see this form */}
                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                  <FieldMultiSelect
                    name="formTags"
                    label="Tags"
                    placeholder="Select tags (optional)"
                    icon={<LocalOfferOutlinedIcon />}
                    options={tagOptions}
                    value={selectedTags}
                    onChange={(_, values) => setSelectedTags(values)}
                  />
                  <Typography sx={{ mt: 0.75, fontSize: 12.5, color: "#64748b" }}>
                    Contacts with any of these tags will see this form.
                  </Typography>
                </Grid>
              </Grid>
            )}

            {/* Step 1: Build Form */}
            {activeStep === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Context strip */}
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%", wordBreak: "break-word", px: 1.1, py: 0.4, borderRadius: 5, fontSize: 12.5, fontWeight: 700, color: QR.main, bgcolor: `${QR.main}10`, border: `1px solid ${QR.main}33` }}>
                    <ApartmentRoundedIcon sx={{ fontSize: 15 }} /><Box component="span" sx={{ fontWeight: 600, opacity: 0.75 }}>Organisation:</Box>{selectedOrg?.organization_name}
                  </Box>
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%", wordBreak: "break-word", px: 1.1, py: 0.4, borderRadius: 5, fontSize: 12.5, fontWeight: 700, color: QR.purple, bgcolor: `${QR.purple}10`, border: `1px solid ${QR.purple}26` }}>
                    <DynamicFormOutlinedIcon sx={{ fontSize: 15 }} /><Box component="span" sx={{ fontWeight: 600, opacity: 0.75 }}>Form Name:</Box>{formName}
                  </Box>
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.1, py: 0.4, borderRadius: 5, fontSize: 12.5, fontWeight: 700, color: "#475569", bgcolor: "#f1f5f9" }}>
                    <Box component="span" sx={{ fontWeight: 600, opacity: 0.75 }}>Fields:</Box>{fields.length}
                  </Box>
                </Stack>


                {/* One card: "Add a field" header + the fields added so far */}
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: fields.length ? "#fff" : "#f8fbff",
                    border: fields.length
                      ? `1px solid ${errors.mode ? "#ef4444" : QR.border}`
                      : `1.5px dashed ${errors.mode ? "#ef4444" : "#93c5fd"}`,
                    boxShadow: fields.length ? "0 1px 3px rgba(16,24,40,0.06)" : "none",
                  }}
                >
                {/* Field Type Selector */}
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, p: { xs: 1.5, sm: 2 }, bgcolor: "#f8fbff", borderBottom: fields.length ? `1px solid ${QR.border}` : "none" }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: "auto" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${QR.blue}, #3b82f6)` }}>
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Add a field</Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>Pick a field type to add it to the form</Typography>
                    </Box>
                  </Stack>
                  <FormControl size="small" sx={{ ...inputSx, width: { xs: "100%", sm: 240 } }}>
                    <Select
                      id="select-type"
                      value={mode}
                      displayEmpty
                      onChange={(e) => handleAddField(e.target.value)}
                      error={!!errors.mode}
                      IconComponent={KeyboardArrowDownRoundedIcon}
                      renderValue={(v) => v || <Box component="span" sx={{ color: "#9ca3af" }}>Select Field Type</Box>}
                      MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: 2.5, border: `1px solid ${QR.border}`, boxShadow: "0 12px 28px -12px rgba(15,23,42,0.3)" } } }}
                    >
                      {
                        formFieldTypes.map((item) => (
                          <MenuItem value={item.form_field_type} sx={{ fontSize: 13.5, gap: 1 }}>
                            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: fieldModeColor(item.form_field_type) }} />
                            {item.form_field_type}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Box>

                {/* Fields */}
                {fields.length > 0 && (
                  <Stack spacing={1.25} sx={{ p: { xs: 1.5, sm: 2 } }}>
                    {fields && fields.map((field, index) => {
                      const mc = fieldModeColor(field.mode);
                      const hasOptions = ["radio", "checkbox", "Dropdown-Single", "Dropdown-Multi"].includes(field.mode);
                      return (
                        <Box
                          key={index}
                          sx={{
                            p: { xs: 1.5, sm: 1.75 },
                            borderRadius: 3,
                            bgcolor: "#fff",
                            border: `1px solid ${QR.border}`,
                            borderLeft: `3px solid ${mc}`,
                            boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                          }}
                        >
                          {/* Field header: number + type + mandatory + delete */}
                          {/* wraps on phones so Mandatory + delete never push past the card edge */}
                          <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            <Box component="span" sx={{ width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 800, color: "#fff", bgcolor: mc }}>
                              {index + 1}
                            </Box>
                            {/* Field Type */}
                            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 5, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: mc, bgcolor: `${mc}12`, border: `1px solid ${mc}30` }}>
                              {field.mode}
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            {/* Mandatory Checkbox */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={field.isRequired}
                                  onChange={(e) =>
                                    handleFieldChange(index, "isRequired", e.target.checked)
                                  }
                                  icon={<RadioButtonUncheckedRoundedIcon sx={{ fontSize: 17 }} />}
                                  checkedIcon={<CheckCircleRoundedIcon sx={{ fontSize: 17 }} />}
                                  sx={{ p: 0.4, color: "#cbd5e1", "&.Mui-checked": { color: "#dc2626" } }}
                                />
                              }
                              label={
                                <Stack direction="row" alignItems="center" spacing={0.4}>
                                  <span>Mandatory</span>
                                  {field.isRequired && <Box component="span" sx={{ color: "#dc2626", fontWeight: 900 }}>*</Box>}
                                </Stack>
                              }
                              sx={{
                                m: 0,
                                pl: 0.4,
                                pr: 1.25,
                                height: 32,
                                borderRadius: 5,
                                border: `1.5px solid ${field.isRequired ? "#fca5a5" : "#e2e8f0"}`,
                                bgcolor: field.isRequired ? "#fef2f2" : "#fff",
                                transition: "all .15s ease",
                                "& .MuiFormControlLabel-label": { fontSize: 12.5, fontWeight: 700, color: field.isRequired ? "#b91c1c" : "#64748b" },
                                "&:hover": field.isRequired ? { borderColor: "#f87171" } : { borderColor: "#93c5fd", "& .MuiFormControlLabel-label": { color: QR.blue } },
                              }}
                            />
                            {/* Delete Button */}
                            <Tooltip title={`Delete This ${field.mode}`}>
                              <IconButton
                                onClick={() => handleDeleteField(index, field.mode)}
                                sx={{ width: 32, height: 32, borderRadius: 2, color: "#dc2626", bgcolor: "#fef2f2", border: "1px solid #fecaca", "&:hover": { color: "#fff", bgcolor: "#dc2626" } }}
                              >
                                <DeleteIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          <Grid container spacing={1.5}>
                            {/* Label or Text Input */}
                            {["text", "number", "date", "textarea"].includes(field.mode) && (
                              <Grid item size={{ xs: 12, sm: 8, md: 6 }}>
                                <Typography component="label" sx={labelSx}>Label</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder={`Enter ${field.mode} label`}
                                  value={field.label}
                                  onChange={(e) => handleFieldChange(index, "label", e.target.value)}
                                  multiline={field.mode === "textarea"}
                                  minRows={field.mode === "textarea" ? 2 : 1}
                                  error={!!errors[`field_${index}_label`]}
                                  sx={inputSx}
                                />
                              </Grid>
                            )}

                            {/* length */}
                            {
                              !["radio", "checkbox", "date", "Dropdown-Single", "Dropdown-Multi"].includes(field.mode) && (
                                <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                                  <Typography component="label" sx={labelSx}>Length</Typography>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Max chars"
                                    value={field.field_length}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => {
                                      let value = e.target.value;

                                      // Remove non-numeric characters
                                      value = value.replace(/\D/g, "");

                                      // Limit to max 2 digits
                                      if (value.length > 3) {
                                        value = value.slice(0, 3);
                                      }

                                      handleFieldChange(index, "field_length", value);
                                    }}
                                    sx={inputSx}
                                  />
                                </Grid>
                              )
                            }

                            {/* For fields with options (Dropdown, radio, checkbox) */}
                            {hasOptions && (
                              <Grid item size={{ xs: 12 }}>
                                {/* Label + options on one line */}
                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
                                {/* Label Input */}
                                <Box sx={{ width: { xs: "100%", sm: 240 }, flexShrink: 0 }}>
                                  <Typography component="label" sx={labelSx}>Label</Typography>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={`Enter ${field.mode} label`}
                                    value={field.label}
                                    onChange={(e) => handleFieldChange(index, "label", e.target.value)}
                                    error={!!errors[`field_${index}_label`]}
                                    sx={inputSx}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography component="label" sx={labelSx}>Options</Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                    {/* Options Inline */}
                                    {field.options.map((opt, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          pl: 1,
                                          pr: 0.5,
                                          height: 42,
                                          boxSizing: "border-box",
                                          borderRadius: 2.5,
                                          bgcolor: "#f8fafc",
                                          border: "1.5px solid #e5e7eb",
                                          "&:focus-within": { borderColor: QR.blue, bgcolor: "#fff" },
                                        }}
                                      >
                                        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 800, color: mc }}>{i + 1}.</Box>
                                        <InputBase
                                          value={opt.label || opt}
                                          onChange={(e) => handleOptionChange(index, i, e.target.value)}
                                          placeholder={`Option ${i + 1}`}
                                          sx={{ width: { xs: 120, sm: 150 }, fontSize: 13.5 }}
                                        />
                                        {i !== 0 && (
                                          <Tooltip title={`Delete this ${i + 1} Option`}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDeleteOption(index, i)}
                                              sx={{ width: 24, height: 24, color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}
                                            >
                                              <CloseRoundedIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ))}

                                    {/* + Add Option Button */}
                                    <Button
                                      size="small"
                                      onClick={() => handleAddOption(index)}
                                      startIcon={<AddRoundedIcon />}
                                      // disabled={!field.field_length || field.options.length >= field.field_length}
                                      disabled={field.options.length === 20}
                                      sx={{ height: 42, px: 1.5, whiteSpace: "nowrap", borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, color: QR.blue, bgcolor: `${QR.blue}0a`, border: `1.5px dashed ${QR.blue}80`, "&:hover": { bgcolor: `${QR.blue}14`, borderColor: QR.blue }, "&.Mui-disabled": { color: "#94a3b8", borderColor: "#e2e8f0" } }}
                                    >
                                      Add Option
                                    </Button>
                                  </Box>
                                </Box>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                </Box>
              </Box>
            )}

            {/* Step 2: Preview */}
            {activeStep === 2 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  bgcolor: "#fff",
                  border: `1px solid ${QR.border}`,
                  "& .MuiOutlinedInput-root": { borderRadius: 2.5 },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb", borderWidth: "1.5px" },
                  "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": { color: QR.blue },
                }}
              >
                {/* header row: form name + organisation (left), field count (right) */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.25,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    bgcolor: "#f8fafc",
                    borderBottom: `1px solid ${QR.border}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${QR.purple}, #a78bfa)` }}>
                      <DynamicFormOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>{formName}</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: QR.main }}>
                        <ApartmentRoundedIcon sx={{ fontSize: 14 }} />
                        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>{selectedOrg?.organization_name}</Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box component="span" sx={{ px: 1.1, py: 0.35, borderRadius: 5, fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", bgcolor: "#fff", border: `1px solid ${QR.border}` }}>
                      Preview
                    </Box>
                    <Box component="span" sx={{ px: 1.1, py: 0.35, borderRadius: 5, fontSize: 12, fontWeight: 800, color: QR.blue, bgcolor: `${QR.blue}12` }}>
                      {fields.length} field{fields.length === 1 ? "" : "s"}
                    </Box>
                  </Stack>
                </Box>

                {/* fields */}
                <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                  <Grid container spacing={2}>
                    {fields.map((f, i) => renderPreviewField(f, i))}
                  </Grid>
                </Box>
              </Paper>
            )}
          </Box>

          {/* ---------- Footer: Back / Next / Submit ---------- */}
          <Box sx={stepActionsSx}>
            <Stack direction="row" spacing={1.5}>
              {activeStep > 0 && (
                <Button onClick={handleBack} disabled={activeStep === 0} sx={backBtnSx}>Back</Button>
              )}
              {activeStep < 2 ? (
                <Button onClick={handleNext} endIcon={<ArrowForwardRoundedIcon />} sx={nextBtnSx}>Next</Button>
              ) : (
                <div style={{ cursor: status === "loading" ? "not-allowed" : "pointer" }}>
                  <Button
                    disabled={status === "loading"}
                    onClick={status === "loading" ? null : handleSubmit}
                    startIcon={status === "loading" ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <CheckRoundedIcon />}
                    sx={nextBtnSx}
                  >
                    {status === "loading" ? "Submitting.." : "Submit"}
                  </Button>
                </div>
              )}
            </Stack>
          </Box>
        </Box>
      </PageWrap>

      <SnackbarMessage
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
      />
    </>
  );
}